import { ChevronRight } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

/* Base UI (`@base-ui/react`): no primitive is used. A disclosure is one button
   and one conditional child — there is no focus trap, no positioning and no
   roving index to inherit, and the headless version would still need every line
   of the unmount argument below written by hand. */

/* A shut block of fields, with a count of what is inside it.
 *
 * The quote forms' answer to a form that asked twenty questions to record
 * six: the rare fields are still THERE, on the step that owns their subject,
 * behind one press. Nothing is removed — a field the form stops sending is a
 * field the next whole-record PUT silently empties.
 *
 * ── Why the count is not decoration ──────────────────────────────────────────
 *
 * A collapsed block with no number is a block nobody opens, because there is no
 * way to tell whether it holds one checkbox or fifteen fields. The count is what
 * makes the shut state honest, and it is inside the accessible name rather than
 * beside it so a screen reader announces "More, 6 fields" as one thing.
 *
 * ── Why the children are not rendered while shut ─────────────────────────────
 *
 * Not `hidden`, not zero height: unmounted. A field that exists in the DOM but
 * cannot be seen is one that still takes tab focus in some browsers and still
 * fires validation, which is how a shut block came to block a submit with an
 * error nobody could find.
 *
 * The cost is that typed-but-unsaved state inside a disclosure is lost when it
 * is shut. That is acceptable HERE and would not be everywhere: every disclosure
 * on a quote form holds fields bound to a draft that lives on the page, not to
 * the disclosure.
 */
export function Disclosure({
  summary,
  count,
  defaultOpen = false,
  children,
  className,
}: {
  summary: string
  /** How many fields are inside. Announced with the summary. */
  count: number
  defaultOpen?: boolean
  children: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div
      className={cn(
        'border-line-hair bg-surface rounded-[var(--radius-DEFAULT)] border',
        className,
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className="text-text-body hover:bg-surface-hover flex w-full items-center gap-2 rounded-[var(--radius-DEFAULT)] px-3.5 py-2.5 text-left text-[13.5px]"
      >
        <ChevronRight
          size={14}
          aria-hidden
          className={cn(
            'text-text-muted shrink-0 transition-transform',
            open && 'rotate-90',
          )}
        />
        <span>{summary}</span>
        <span className="text-text-muted asa-num text-[12.5px]">({count})</span>
      </button>

      {open ? (
        <div className="border-line-hair grid gap-4 border-t px-3.5 py-3.5 sm:grid-cols-2 lg:grid-cols-3">
          {children}
        </div>
      ) : null}
    </div>
  )
}
